// LayoutEditorSlotNode.jsx

import React from 'react';

// Utilities
import { getViewBlockDisplayTitle } from '../pageEditorLayoutUtils';
import { getNodeBlockKey } from '../pageEditorLayoutUtils';
import {
	createEmptyRichTextDocument,
	normalizeRichTextValue,
	serializeRichTextDocument,
} from 'utilities/RichText';

// Components
import { getLayoutBlockPlaceholderLabel } from '../layoutBlockPlaceholders';
import { buildFieldBlockRows } from '../layoutFieldDisplay';
import { normalizePageNodePresentation } from '../LayoutNodePresentation';
import LayoutEditorSlotShell from '../slotshell/LayoutEditorSlotShell';
import LayoutEditorSlotContent from './LayoutEditorSlotContent';

function normalizeString(value) {
	return String(value || '').trim();
}

function getViewBlockRelatedTypeKey(block, view = null) {
	return normalizeString(block?.relatedTypeKey || view?.nodeType || view?.nodeTypeKey || view?.typeKey);
}

const EMPTY_RICH_TEXT_SERIALIZED = serializeRichTextDocument(createEmptyRichTextDocument());

function isRichTextValueEmpty(value) {
	const normalizedValue = normalizeRichTextValue(value);
	return serializeRichTextDocument(normalizedValue.content) === EMPTY_RICH_TEXT_SERIALIZED;
}

/**
 * LayoutEditorSlotNode
 *
 * Renders one layout editor slot shell together with its resolved preview content.
 *
 * @param {object} handlers - Interaction handlers for the slot overlay and inline editing lifecycle.
 * @param {object} interactionState - Drag, hover, toolbar, and inline rich-text state for the active editor surface.
 * @param {string} language - Language key passed into compact view rendering.
 * @param {string} parentKey - Parent container key for slot hover and toolbar targeting.
 * @param {object} previewData - Preview maps and related data needed to resolve slot content.
 * @param {object} resolvers - Functions used to resolve slot controls, blocks, rollups, and types.
 * @param {object|null} slotNode - The slot node being rendered.
 * @param {object} slotOptions - Context describing the slot position within its parent container.
 *
 * @returns {ReactNode} The rendered slot shell and content.
 */
const LayoutEditorSlotNode = ({
	embeddedInPageHybrid = false,
	fieldRowUsePanelSurface = false,
	handlers = {},
	interactionState = {},
	language = 'en',
	onItemClick = null,
	parentKey = '',
	previewData = {},
	resolvers = {},
	slotNode = null,
	slotOptions = {},
	typeLabelPlural = 'records',
	useBlockPlaceholders = false,
	viewBlockEnforceExplicitProperties = true,
}) => {
	const {
		handleClearHoveredSlot,
		handleDragEnd,
		handleDragStart,
		handleEmptySlotDragOver,
		handleEmptySlotDrop,
		handleInlineRichTextBlur,
		handleOpenPagePreviewItem,
		handleOpenToolbar,
		handleSetHoveredSlot,
		handleStartInlineRichTextEditing,
		setInlineRichTextDraftValue,
	} = handlers;
	const {
		dragOverTarget,
		draggingNodeKey,
		hoveredSlotState,
		inlineRichTextDraftValue,
		inlineRichTextPlaceholder = 'Write page copy...',
		inlineRichTextSlotKey,
		toolbarRowKey,
		toolbarSlotKey,
	} = interactionState;
	const {
		fieldBlockTypeDef,
		nodeByKey,
		previewNode,
		pagePlaybackContext,
		rollupPreviewBySelectionKey,
		uploadErrorBySlotKey,
		uploadingSlotKey,
		viewPreviewByBlockKey,
	} = previewData;
	const {
		getResolvedBlockValue,
		getResolvedRichTextBlockValue,
		getSlotControls,
		getType,
	} = resolvers;
	const {
		block,
		blockType,
		isContentZoneBlock,
		isCoverBlock,
		isDiscussionBlock,
		isFieldsBlock,
		isNodeBlock,
		isRichTextBlock,
		isRollupBlock,
		isVideoBlock,
		isViewBlock,
		primaryToolbarAction,
	} = getSlotControls(slotNode, slotOptions);
	const isDragging = draggingNodeKey === slotNode?.key;
	const isEmptySlot = !blockType;
	const isDropActive = isEmptySlot
		&& dragOverTarget?.edge === 'empty-slot'
		&& dragOverTarget?.parentKey === parentKey
		&& dragOverTarget?.targetNodeKey === slotNode?.key;
	const isSlotHovered = hoveredSlotState.rowKey === parentKey && hoveredSlotState.slotKey === slotNode?.key;
	const isSlotActive = toolbarRowKey === parentKey && toolbarSlotKey === slotNode?.key;
	const blockKey = getNodeBlockKey(slotNode);
	const resolvedBlock = getResolvedBlockValue(blockKey, block);
	const viewPreview = blockKey ? viewPreviewByBlockKey.get(blockKey) || null : null;
	const resolvedView = viewPreview?.view || null;
	const resolvedViewNodes = Array.isArray(viewPreview?.nodes) ? viewPreview.nodes : [];
	const viewBlockTitle = getViewBlockDisplayTitle(resolvedBlock, resolvedView, { fallback: 'Selected View' });
	const isViewMissing = isViewBlock && !resolvedView;
	const resolvedViewRelatedTypeKey = getViewBlockRelatedTypeKey(resolvedBlock, resolvedView);
	const isViewTypePending = Boolean(resolvedView && resolvedViewRelatedTypeKey && !viewPreview?.typeDef);
	const isViewEmpty = Boolean(viewPreview?.typeDef && resolvedView && resolvedViewNodes.length === 0);
	const isViewReady = Boolean(viewPreview?.typeDef && resolvedView && resolvedViewNodes.length > 0);
	const fieldBlockRows = isFieldsBlock
		? buildFieldBlockRows({
			fieldKeys: resolvedBlock?.fieldKeys,
			item: previewNode,
			nodesByKey: nodeByKey,
			typeDef: fieldBlockTypeDef,
		})
		: [];
	const fieldsBlockTitle = String(resolvedBlock?.titleOverride || '').trim();
	const isFieldsPreviewUnavailable = isFieldsBlock && (!previewNode || !fieldBlockTypeDef);
	const nodeBlockKey = String(resolvedBlock?.nodeKey || '').trim();
	const resolvedNode = nodeBlockKey ? nodeByKey[nodeBlockKey] || null : null;
	const nodePresentation = normalizePageNodePresentation(resolvedBlock?.presentation);
	const nodeBlockTitle = String(resolvedBlock?.titleOverride || resolvedNode?.title || 'Selected Node').trim() || 'Selected Node';
	const videoBlockTitle = String(resolvedBlock?.titleOverride || resolvedNode?.title || '').trim();
	const isVideoUploading = isVideoBlock && uploadingSlotKey === slotNode?.key;
	const videoUploadError = isVideoBlock ? String(uploadErrorBySlotKey?.[slotNode?.key] || '').trim() : '';
	const isNodeMissing = isNodeBlock && !resolvedNode;
	const isVideoMissing = isVideoBlock && !resolvedNode && !isVideoUploading;
	const richTextValue = isRichTextBlock ? getResolvedRichTextBlockValue(blockKey, block) : null;
	const isRichTextEmpty = isRichTextBlock ? isRichTextValueEmpty(richTextValue) : false;
	const isInlineRichTextEditing = isRichTextBlock && inlineRichTextSlotKey === slotNode?.key;
	const isActivePagePreviewView = isViewBlock && isSlotActive;
	const resolvedRichTextValue = isInlineRichTextEditing ? inlineRichTextDraftValue : richTextValue;
	const rollupTypeKey = String(resolvedBlock?.typeKey || '').trim();
	const rollupBlockKey = String(resolvedBlock?.rollupKey || '').trim();
	const rollupSelectionKey = rollupTypeKey && rollupBlockKey ? `${rollupTypeKey}:${rollupBlockKey}` : '';
	const rollupPreview = rollupSelectionKey ? rollupPreviewBySelectionKey.get(rollupSelectionKey) || null : null;
	const resolvedRollup = rollupPreview?.rollup || null;
	const resolvedRollupResult = rollupPreview?.result || null;
	const rollupTypeLabel = String(getType(rollupTypeKey)?.labelPlural || getType(rollupTypeKey)?.label || rollupTypeKey || 'Items').trim() || 'Items';
	const rollupBlockTitle = String(resolvedBlock?.titleOverride || resolvedRollup?.title || 'Selected Rollup').trim() || 'Selected Rollup';
	const isRollupMissing = isRollupBlock && !resolvedRollup;
	const discussionBlockTitle = String(resolvedBlock?.titleOverride || 'Discussion').trim() || 'Discussion';
	const contentZoneTitle = String(resolvedBlock?.titleOverride || 'Content section').trim() || 'Content section';
	const suppressBlockPlaceholder = Boolean(previewNode) && Boolean(blockType);
	const blockPlaceholderLabel = useBlockPlaceholders && !suppressBlockPlaceholder && blockType && !isRichTextBlock
		? getLayoutBlockPlaceholderLabel(resolvedBlock)
		: (
			useBlockPlaceholders && !suppressBlockPlaceholder && isRichTextBlock && isRichTextEmpty && !isInlineRichTextEditing
				? 'RICH TEXT'
				: null
		);

	// ----- RENDER ----- //

	return (
		<LayoutEditorSlotShell
			isDragging={isDragging}
			isDropActive={isDropActive}
			isOverlayHidden={isInlineRichTextEditing || isActivePagePreviewView}
			isPreviewInteractive={isInlineRichTextEditing || isActivePagePreviewView}
			isSlotActive={isSlotActive}
			isSlotHovered={isSlotHovered}
			onClick={(event) => {
				event.stopPropagation();

				if (isRichTextBlock) {
					if (!isInlineRichTextEditing) {
						handleStartInlineRichTextEditing(slotNode?.key, richTextValue);
					}

					return;
				}

				handleOpenToolbar(parentKey, slotNode?.key, event);
			}}
			onDoubleClick={primaryToolbarAction?.onClick
				? (event) => {
					event.stopPropagation();
					primaryToolbarAction.onClick();
				}
				: undefined}
			onDragEnd={handleDragEnd}
			onDragOver={isEmptySlot
				? (event) => handleEmptySlotDragOver(parentKey, slotNode?.key, event)
				: undefined}
			onDragStart={(event) => handleDragStart(slotNode?.key, event)}
			onDrop={isEmptySlot
				? (event) => handleEmptySlotDrop(slotNode?.key, event)
				: undefined}
			onMouseEnter={() => handleSetHoveredSlot(parentKey, slotNode?.key)}
			onMouseLeave={() => handleClearHoveredSlot(parentKey, slotNode?.key)}
		>
			<LayoutEditorSlotContent
				blockPlaceholderLabel={blockPlaceholderLabel}
				contentZoneTitle={contentZoneTitle}
				discussionBlockTitle={discussionBlockTitle}
				fieldBlockRows={fieldBlockRows}
				fieldRowUsePanelSurface={fieldRowUsePanelSurface}
				fieldsBlockTitle={fieldsBlockTitle}
				inlineRichTextPlaceholder={inlineRichTextPlaceholder}
				inlineRichTextValue={resolvedRichTextValue}
				isContentZoneBlock={isContentZoneBlock}
				isCoverBlock={isCoverBlock}
				isDiscussionBlock={isDiscussionBlock}
				isFieldsBlock={isFieldsBlock}
				isFieldsPreviewUnavailable={isFieldsPreviewUnavailable}
				isInlineRichTextEditing={isInlineRichTextEditing}
				isRichTextToolbarActive={isRichTextBlock && isSlotActive}
				isNodeBlock={isNodeBlock}
				isNodeMissing={isNodeMissing}
				isRichTextBlock={isRichTextBlock}
				isRichTextEmpty={isRichTextEmpty}
				isRollupBlock={isRollupBlock}
				isRollupMissing={isRollupMissing}
				isVideoBlock={isVideoBlock}
				isVideoMissing={isVideoMissing}
				isVideoUploading={isVideoUploading}
				isViewBlock={isViewBlock}
				isViewEmpty={isViewEmpty}
				isViewMissing={isViewMissing}
				isViewReady={isViewReady}
				isViewTypePending={isViewTypePending}
				language={language}
				nodeBlockTitle={nodeBlockTitle}
				nodePresentation={nodePresentation}
				pagePlaybackContext={pagePlaybackContext}
				resolvedFieldsNode={previewNode}
				resolvedNode={resolvedNode}
				resolvedRollupResult={resolvedRollupResult}
				resolvedView={resolvedView}
				resolvedViewNodes={resolvedViewNodes}
				richTextValue={resolvedRichTextValue}
				rollupBlockTitle={rollupBlockTitle}
				rollupTypeLabel={rollupTypeLabel}
				typeLabelPlural={typeLabelPlural}
				videoBlockTitle={videoBlockTitle}
				videoUploadError={videoUploadError}
				viewBlockEnforceExplicitProperties={viewBlockEnforceExplicitProperties}
				viewBlockTitle={viewBlockTitle}
				viewTypeDef={viewPreview?.typeDef || null}
				onInlineRichTextBlur={(nextValue) => handleInlineRichTextBlur(slotNode?.key, nextValue)}
				onInlineRichTextChange={setInlineRichTextDraftValue}
				onRichTextMenuBarClick={(event) => {
					event.stopPropagation();
					handleOpenToolbar(parentKey, slotNode?.key, event);
				}}
				onViewItemClick={(node) => {
					if (
						typeof onItemClick === 'function'
						&& embeddedInPageHybrid
						&& !isInlineRichTextEditing
					) {
						onItemClick(node);
						return;
					}

					handleOpenPagePreviewItem(node, resolvedView, viewPreview?.typeDef || null);
				}}
			/>
		</LayoutEditorSlotShell>
	);
};

// Dev Tools
LayoutEditorSlotNode.displayName = 'LayoutEditorSlotNode';

export default LayoutEditorSlotNode;
